'use client';

import { motion } from 'framer-motion';
import { Mail, Phone } from 'lucide-react';
import Image from 'next/image';
import Link from 'next/link';
import Script from 'next/script';

import { DownloadQr, LogoWhiteImage } from '@/assets';

import AppStore from '../widgets/AppStore';
import GooglePlay from '../widgets/GooglePlay';

const companyInfo = {
  name: 'CÔNG TY TNHH GIẢI PHÁP PHẦN MỀM RIZERS - SOFT',
  desc: 'Zump là giải pháp quản lý bán hàng FnB đơn giản nhất, siêu nhỏ gọn và siêu tiện lợi, cung cấp đủ tính năng cần thiết.',
  address:
    '72/19/8/29 Đường số 4, khu phố 6 , Phường Hiệp Bình Phước, Thành phố Thủ Đức, Thành phố Hồ Chí Minh, Việt Nam',
  registrationNumber: '0317652639',
  supportEmail: 'cskh@zump.vn',
  supportPhone: '0374 828 046',
  socialMedia: [
    {
      platform: 'Facebook',
      url: 'https://www.facebook.com/zump.vn',
      icon: '/assets/images/facebook.svg',
    },
    {
      platform: 'Youtube',
      url: 'https://www.youtube.com/@Zumpvn',
      icon: '/assets/images/youtube.svg',
    },
    {
      platform: 'Zalo',
      url: 'https://zalo.me/2072263478653960668',
      icon: '/assets/images/zalo.svg',
    },
  ],
};

export default function Footer() {
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
        delayChildren: 0.2,
      },
    },
  };

  const itemVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: {
        duration: 0.5,
      },
    },
  };

  return (
    <footer
      id="contact"
      className="relative bg-gradient-to-b from-gray-900 to-gray-950 py-12 text-white max-sm:pb-24"
    >
      <div className="container relative z-10 mx-auto px-4">
        <motion.div
          className="grid grid-cols-1 gap-8 lg:grid-cols-8 lg:gap-12 xl:grid-cols-12"
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
        >
          <motion.div
            className="col-span-full lg:col-span-4"
            variants={itemVariants}
          >
            <Link href="/" className="mb-6 block">
              <motion.div
                whileHover={{ scale: 1.05 }}
                transition={{ duration: 0.2 }}
              >
                <Image
                  src={LogoWhiteImage}
                  alt="Zump Logo"
                  width={180}
                  height={60}
                  className="h-14 w-auto"
                />
              </motion.div>
            </Link>
            <p className="mb-4 mt-5 max-w-md text-left leading-relaxed text-gray-300">
              {companyInfo.desc}
            </p>
            <div className="space-y-1.5 text-sm">
              <p className="text-left text-gray-300">{companyInfo.name}</p>
              <p className="text-left text-gray-300">{companyInfo.address}</p>
              <p className="text-left text-gray-300">
                MST: {companyInfo.registrationNumber}
              </p>
            </div>
            <div className="relative mt-2 h-5 w-24">
              <Link
                href="//www.dmca.com/Protection/Status.aspx?ID=7a1fecf9-e82e-4542-b0e3-5655164de31c"
                title="DMCA.com Protection Status"
                target="_blank"
              >
                <Image
                  fill
                  src="https://images.dmca.com/Badges/dmca-badge-w100-5x1-01.png?ID=7a1fecf9-e82e-4542-b0e3-5655164de31c"
                  alt="DMCA.com Protection Status"
                />
              </Link>
              <Script src="https://images.dmca.com/Badges/DMCABadgeHelper.min.js" />
            </div>
          </motion.div>

          <div className="md:col-span-8">
            <div className="grid grid-cols-1 gap-8 md:grid-cols-3 lg:grid-cols-4">
              <motion.div variants={itemVariants}>
                <h3 className="relative mb-6 inline-block text-xl font-semibold text-white">
                  Liên kết nhanh
                  <span className="absolute -bottom-1.5 left-0 h-0.5 w-1/2 bg-yellow-600"></span>
                </h3>
                <ul className="space-y-3">
                  <li>
                    <Link href="/">
                      <span className="text-gray-300 transition-colors hover:text-yellow-600">
                        Giải pháp
                      </span>
                    </Link>
                  </li>
                  <li>
                    <Link href="/#features">
                      <span className="text-gray-300 transition-colors hover:text-yellow-600">
                        Tính năng
                      </span>
                    </Link>
                  </li>
                  <li>
                    <Link href="/#pricing">
                      <span className="text-gray-300 transition-colors hover:text-yellow-600">
                        Bảng giá
                      </span>
                    </Link>
                  </li>
                  {/* <li>
                    <Link href="/about">
                      <span className="text-gray-300 transition-colors hover:text-yellow-600">
                        Về chúng tôi
                      </span>
                    </Link>
                  </li> */}
                  {/* <li>
                    <Link href="/contact">
                      <span className="text-gray-300 transition-colors hover:text-white">
                        Liên hệ
                      </span>
                    </Link>
                  </li> */}
                </ul>
              </motion.div>

              <motion.div variants={itemVariants}>
                <h3 className="relative mb-6 inline-block text-xl font-semibold text-white">
                  Liên hệ
                  <span className="absolute -bottom-1.5 left-0 h-0.5 w-1/2 bg-yellow-600"></span>
                </h3>
                <ul className="space-y-4">
                  <li className="group flex items-start text-white">
                    <div className="mr-3 mt-0.5 rounded-full bg-gray-800 p-2">
                      <Phone className="group-hover:text-yellow-600" />
                    </div>
                    <div>
                      <p className="text-left text-sm text-white group-hover:text-yellow-600">
                        Hotline
                      </p>
                      <p className="text-left text-gray-300 transition-colors group-hover:text-yellow-600">
                        {companyInfo.supportPhone}
                      </p>
                    </div>
                  </li>
                  <li className="group flex items-start text-white">
                    <div className="mr-3 mt-0.5 rounded-full bg-gray-800 p-2">
                      <Mail className="group-hover:text-yellow-600" />
                    </div>
                    <div>
                      <p className="text-left text-sm text-white group-hover:text-yellow-600">
                        Email
                      </p>
                      <p className="text-left text-gray-300 transition-colors group-hover:text-yellow-600">
                        {companyInfo.supportEmail}
                      </p>
                    </div>
                  </li>
                </ul>
                <div className="mt-6">
                  <h4 className="mb-3 text-left text-base font-medium text-white">
                    Kết nối với chúng tôi
                  </h4>
                  <div className="flex space-x-3">
                    {companyInfo.socialMedia.map((social) => (
                      <motion.a
                        key={social.platform}
                        href={social.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="relative size-10 overflow-hidden"
                        whileHover={{ y: -5 }}
                        whileTap={{ scale: 0.95 }}
                      >
                        <Image src={social.icon} alt={social.platform} fill />
                      </motion.a>
                    ))}
                  </div>
                </div>
              </motion.div>

              <motion.div className="lg:col-span-2" variants={itemVariants}>
                <h3 className="relative mb-6 inline-block text-xl font-semibold text-white">
                  Tải ứng dụng
                  <span className="absolute -bottom-1.5 left-0 h-0.5 w-1/2 bg-yellow-600"></span>
                </h3>
                <p className="mb-5 text-left text-lg leading-relaxed text-white">
                  Tải ứng dụng để được trải nghiệm tốt nhất và nhận nhiều ưu đãi
                  hơn.
                </p>
                <motion.div
                  className="flex flex-wrap items-center gap-4"
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: 0.5 }}
                >
                  <div className="relative aspect-square size-24 md:size-28 lg:size-32">
                    <Image
                      src={DownloadQr}
                      alt="QR Code"
                      fill
                      className="rounded-lg"
                    />
                  </div>
                  <div className="flex flex-col gap-4">
                    <AppStore />
                    <GooglePlay />
                  </div>
                </motion.div>
              </motion.div>
            </div>
          </div>
        </motion.div>

        {/* Bottom Bar */}
        <div className="mt-16 border-t border-gray-800/50 pt-8">
          <div className="flex flex-col items-center justify-between gap-2 max-sm:flex-col-reverse md:flex-row">
            <div>
              <motion.p
                className="text-sm text-gray-500"
                initial={{ opacity: 0 }}
                whileInView={{ opacity: 1 }}
                viewport={{ once: true }}
                transition={{ delay: 0.5 }}
              >
                ©2023 Zump.vn. All Rights Reserved
              </motion.p>
              <motion.p
                className="text-sm text-gray-500"
                initial={{ opacity: 0 }}
                whileInView={{ opacity: 1 }}
                viewport={{ once: true }}
                transition={{ delay: 0.5 }}
              >
                <Link
                  href="https://www.rizerssoft.com/"
                  aria-label="Powered by Rizerssoft Ltd"
                  className="underline"
                  target="_blank"
                >
                  Powered by Rizerssoft Ltd
                </Link>
              </motion.p>
            </div>
            <div className="mt-4 flex space-x-6 md:mt-0">
              <div>
                <motion.div
                  initial={{ opacity: 0 }}
                  whileInView={{ opacity: 1 }}
                  viewport={{ once: true }}
                  transition={{ delay: 0.7 }}
                >
                  <Link
                    href="/privacy-policy/"
                    className="text-sm text-gray-500 transition-colors hover:text-primary-400"
                  >
                    Chính sách bảo mật
                  </Link>
                </motion.div>
                <motion.div
                  initial={{ opacity: 0 }}
                  whileInView={{ opacity: 1 }}
                  viewport={{ once: true }}
                  transition={{ delay: 0.6 }}
                >
                  <Link
                    href="/terms-and-conditions/"
                    className="text-sm text-gray-500 transition-colors hover:text-primary-400"
                  >
                    Điều khoản sử dụng
                  </Link>
                </motion.div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
}
